import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box, Button, Collapse, Rating, TextField, Typography, alpha } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import ReviewFormData from "../models/ReviewFormData";
import ReviewService from "../services/ReviewService";

const service = new ReviewService();
const TEXT_FIELD_ACCENT = "#376ac7";

const ReviewForm = () => {
    const { t } = useTranslation();
    const schema = yup.object({
        userName: yup.string().required(t("reviewForm.validation.nameRequired")).max(100),
        userEmailAddress: yup.string().required(t("reviewForm.validation.emailRequired")).email(t("reviewForm.validation.emailInvalid")).max(150),
        comment: yup.string().max(1000).optional(),
        rating: yup.number().required(t("reviewForm.validation.ratingRequired")).min(1).max(5),
    }) as yup.ObjectSchema<ReviewFormData>;
    const {
        control,
        handleSubmit,
        reset,
        register,
        formState: { errors, isSubmitSuccessful },
    } = useForm<ReviewFormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            userName: "",
            userEmailAddress: "",
            comment: "",
            rating: 0,
        },
    });

    const onSubmit = async (data: ReviewFormData) => {
        try {
            await service.addReview(data);
            reset();
        } catch (err) {
            console.error("Greška pri slanju recenzije:", err);
        }
    };

    return (
        <Box
            sx={(theme) => ({
                mt: 4,
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 18px 40px rgba(0,0,0,0.12)`,
                border: `1px solid ${theme.palette.divider}`,
            })}
        >
            <Typography variant="h5" fontWeight={700} gutterBottom>
                {t("reviewForm.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("reviewForm.subtitle")}
            </Typography>

            <Collapse in={isSubmitSuccessful}>
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                        {t("reviewForm.success")}
                    </Typography>
                </Alert>
            </Collapse>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <TextField
                        label={t("reviewForm.name")}
                        fullWidth
                        {...register("userName")}
                        error={!!errors.userName}
                        helperText={errors.userName?.message}
                        variant="outlined"
                        sx={(theme) => ({
                            "& .MuiInputLabel-root": {
                                color: TEXT_FIELD_ACCENT,
                                "&.Mui-focused": {
                                    color: TEXT_FIELD_ACCENT,
                                },
                            },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.6),
                                "& fieldset": {
                                    borderColor: alpha(theme.palette.text.primary, 0.2),
                                },
                                "&:hover fieldset": {
                                    borderColor: alpha(theme.palette.text.primary, 0.35),
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: TEXT_FIELD_ACCENT,
                                    boxShadow: `0 0 0 3px ${alpha(TEXT_FIELD_ACCENT, 0.2)}`,
                                },
                            },
                            "& .MuiInputBase-input": {
                                color: theme.palette.common.white,
                            },
                        })}
                    />

                    <TextField
                        label={t("reviewForm.email")}
                        fullWidth
                        {...register("userEmailAddress")}
                        error={!!errors.userEmailAddress}
                        helperText={errors.userEmailAddress?.message}
                        variant="outlined"
                        sx={(theme) => ({
                            "& .MuiInputLabel-root": {
                                color: TEXT_FIELD_ACCENT,
                                "&.Mui-focused": {
                                    color: TEXT_FIELD_ACCENT,
                                },
                            },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.default, 0.6),
                                "& fieldset": {
                                    borderColor: alpha(theme.palette.text.primary, 0.2),
                                },
                                "&:hover fieldset": {
                                    borderColor: alpha(theme.palette.text.primary, 0.35),
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: TEXT_FIELD_ACCENT,
                                    boxShadow: `0 0 0 3px ${alpha(TEXT_FIELD_ACCENT, 0.2)}`,
                                },
                            },
                            "& .MuiInputBase-input": {
                                color: theme.palette.common.white,
                            },
                        })}
                    />
                </Box>

                <Controller
                    name="rating"
                    control={control}
                    render={({ field }) => (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                {t("reviewForm.rating")}
                            </Typography>
                            <Rating
                                value={field.value}
                                onChange={(_, value) => field.onChange(value)}
                                size="large"
                            />
                            {errors.rating && (
                                <Typography variant="caption" color="error">
                                    {errors.rating.message}
                                </Typography>
                            )}
                        </Box>
                    )}
                />

                <TextField
                    label={t("reviewForm.comment")}
                    fullWidth
                    multiline
                    rows={4}
                    {...register("comment")}
                    error={!!errors.comment}
                    helperText={errors.comment?.message}
                    variant="outlined"
                    sx={(theme) => ({
                        mb: 2,
                        "& .MuiInputLabel-root": {
                            color: TEXT_FIELD_ACCENT,
                            "&.Mui-focused": {
                                color: TEXT_FIELD_ACCENT,
                            },
                        },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.background.default, 0.6),
                            "& fieldset": {
                                borderColor: alpha(theme.palette.text.primary, 0.2),
                            },
                            "&:hover fieldset": {
                                borderColor: alpha(theme.palette.text.primary, 0.35),
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: TEXT_FIELD_ACCENT,
                                boxShadow: `0 0 0 3px ${alpha(TEXT_FIELD_ACCENT, 0.2)}`,
                            },
                        },
                        "& .MuiInputBase-input": {
                            color: theme.palette.common.white,
                        },
                    })}
                />

                <Button
                    type="submit"
                    variant="contained"
                    sx={{
                        px: 4,
                        py: 1.25,
                        borderRadius: 2,
                        fontWeight: 700,
                        textTransform: "none",
                    }}
                >
                    {t("reviewForm.submit")}
                </Button>
            </form>
        </Box>
    );
};

export default ReviewForm;
